/* eslint-disable max-lines */
import React, { useContext, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import _ from 'lodash';
import moment from '@this/lib/moment';
import { toDomesticTel, isAiTravel } from '@this/src/util';
import { reportError } from '@this/lib/bugsnag';
import { Link } from '@this/shared/ui/navigations/link';
import { Button } from '@this/shared/ui/inputs/button';
import Markdown from '@this/shared/markdown/markdown';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { compareApproveStage } from '@this/domain/approver/approver';
import { formatBakurakuWorkflowRequestCaption } from '@this/domain/bakuraku_workflow_request';
import type ReserveConfirmStore from '@this/src/domain/reserve_confirm_store';
import CoreAppContext from '@this/src/components/core_app/core_app_context';
import { createAITTheme } from '@this/src/components/shared/ui/theme';
import { useAITTheme } from '@this/src/components/shared/ui/theme/provider';
import OrderItemMappingListSection from './order_item_mapping_list_section/order_item_mapping_list_section';
import { ConfirmCustomerConfirmModal } from './confirm_customer_confirm_modal';
import { parse } from '../input_customer_information/input_customer_information';
import TabiregiSection from '../../tabiregi/tabiregi_section';

export type Props = {
  store: ReserveConfirmStore;
  handleSaveDraft?: () => void;
  handlePaymentSubmit: () => void;
  handleBackToStep1Click?: () => void;
};

const isOwnTraveler = (
  traveler: ReserveConfirmStore['reserveInfo']['reservingTrip']['travelers'][number],
  store: ReserveConfirmStore
) => traveler.userId === store.reserveInfo.loginUserId;

const parsePaymentError = (text: string, linkColor: string) =>
  parse(text).replace(/<a /g, `<a style="color: ${linkColor};" `);

const ConfirmCustomerInformation: React.FC<Props> = ({
  store,
  handleSaveDraft,
  handlePaymentSubmit,
  handleBackToStep1Click
}) => {
  const { serviceId } = useContext(CoreAppContext);
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const handleConfirmed = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      handlePaymentSubmit();
    },
    [handlePaymentSubmit]
  );

  try {
    return (
      <StylesContext.Provider value={styles}>
        <>
          <Title data-testid="reserve-confirm-confirm-title">予約内容を確認してください</Title>
          <ChildWrapper data-testid="reserve-confirm-confirm-root">
            {store.isCustomerInfoRequired && (
              <div style={styles.section} data-testid="reserve-confirm-confirm-section-traveler">
                <div style={styles.subtitle} data-testid="reserve-confirm-confirm-subtitle-traveler">
                  出張者
                </div>
                <ChildContent data-testid="reserve-confirm-confirm-content-traveler">
                  {store.reserveInfo.reservingTrip.travelers.map((traveler, i) => (
                    <div key={i} data-testid={`reserve-confirm-confirm-traveler-row-${i}`}>
                      <TravelerPeopleNum>{i + 1}人目</TravelerPeopleNum>
                      <div style={styles.travelerBox}>
                        <div className="flex">
                          <div style={styles.label}>氏名（ローマ字）</div>
                          <div
                            style={styles.label}
                            data-wovn-ignore
                            data-testid={`reserve-confirm-confirm-traveler-name-roman-${i}`}
                          >
                            {traveler.lastNameRoman} {traveler.firstNameRoman}
                          </div>
                        </div>
                        <div className="flex">
                          <div style={styles.label}>氏名（カナ）</div>
                          <div
                            style={styles.label}
                            data-wovn-ignore
                            data-testid={`reserve-confirm-confirm-traveler-name-kana-${i}`}
                          >
                            {traveler.lastNameKana} {traveler.firstNameKana}
                          </div>
                        </div>
                        {store.reserveInfo.reservingTrip.isFlightCustomerInfoRequired && (
                          <div className="flex">
                            <div style={styles.label} data-wovn-ignore>
                              誕生日
                            </div>
                            <div
                              style={styles.label}
                              data-testid={`reserve-confirm-confirm-traveler-birthday-${i}`}
                            >
                              {traveler.type === 'companion' || traveler.userId === store.reserveInfo.loginUserId
                                ? traveler.birthday?.isValid?.()
                                  ? traveler.birthday.format('YYYY-MM-DD')
                                  : ''
                                : '****-**-**(プライバシー保護のため非表示)'}
                            </div>
                          </div>
                        )}
                        {(store.reserveInfo.reservingTrip.isFlightCustomerInfoRequired ||
                          store.reserveInfo.reservingTrip.isHotelInfoRequired) && (
                          <div className="flex">
                            <div style={styles.label}>性別</div>
                            {traveler.flightGender === 'male' ? (
                              <div
                                style={styles.label}
                                data-testid={`reserve-confirm-confirm-traveler-gender-${i}`}
                              >
                                男性
                              </div>
                            ) : traveler.flightGender === 'female' ? (
                              <div
                                style={styles.label}
                                data-testid={`reserve-confirm-confirm-traveler-gender-${i}`}
                              >
                                女性
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </ChildContent>
              </div>
            )}
            {store.reserveInfo.reservingTrip.isFlightCustomerInfoRequired && (
              <Section data-testid="reserve-confirm-confirm-section-flight">
                <Subtitle data-testid="reserve-confirm-confirm-subtitle-flight">航空券</Subtitle>
                <ChildContent data-testid="reserve-confirm-confirm-content-flight">
                  {store.reserveInfo.reservingTrip.travelers.map((traveler, i) => (
                    <div key={i} data-testid={`reserve-confirm-confirm-flight-row-${i}`}>
                      <p style={styles.peopleNum}>{i + 1}人目</p>
                      <div style={styles.travelerBox}>
                        <div className="flex">
                          <div style={styles.label}>氏名（カナ）</div>
                          <div
                            style={styles.label}
                            data-wovn-ignore
                            data-testid={`reserve-confirm-confirm-flight-name-kana-${i}`}
                          >
                            {traveler.flightLastNameKana} {traveler.flightFirstNameKana}
                          </div>
                        </div>
                        {store.reserveInfo.reservingTrip.isForeignFlightCustomerInfoRequired ? (
                          <>
                            <div className="flex">
                              <div style={styles.label}>姓（LastName）</div>
                              <div
                                style={styles.label}
                                data-wovn-ignore
                                data-testid={`reserve-confirm-confirm-flight-last-name-roman-${i}`}
                              >
                                {traveler.flightLastNameRoman}
                              </div>
                            </div>
                            <div className="flex">
                              <div style={styles.label}>ミドルネーム（MiddleName）</div>
                              <div
                                style={styles.label}
                                data-wovn-ignore
                                data-testid={`reserve-confirm-confirm-flight-middle-name-roman-${i}`}
                              >
                                {traveler.flightMiddleNameRoman}
                              </div>
                            </div>
                            <div className="flex">
                              <div style={styles.label}>名（FirstName）</div>
                              <div
                                style={styles.label}
                                data-wovn-ignore
                                data-testid={`reserve-confirm-confirm-flight-first-name-roman-${i}`}
                              >
                                {traveler.flightFirstNameRoman}
                              </div>
                            </div>
                            <SmallGray>
                              ※1文字でもパスポートの表記と異なるとご搭乗いただけません。
                              <br />
                              ※各航空会社の定めにより予約後の訂正はできないため、キャンセルと新たな予約が必要となります。その場合規定の手数料が発生いたします。
                              {isAiTravel(serviceId) && (
                                <>
                                  手数料の詳細については
                                  <Link href="/charges" target="_blank" isExternal>
                                    旅行業務取扱い料金表
                                  </Link>
                                  をご確認ください。
                                </>
                              )}
                            </SmallGray>
                          </>
                        ) : (
                          <>
                            <div className="flex">
                              <div style={styles.label}>氏名（ローマ字）</div>
                              <div
                                style={styles.label}
                                data-wovn-ignore
                                data-testid={`reserve-confirm-confirm-flight-name-roman-${i}`}
                              >
                                <span data-testid={`reserve-confirm-confirm-flight-last-name-roman-${i}`}>
                                  {traveler.flightLastNameRoman}
                                </span>{' '}
                                <span data-testid={`reserve-confirm-confirm-flight-middle-name-roman-${i}`}>
                                  {traveler.flightMiddleNameRoman}
                                </span>
                                {traveler.flightMiddleNameRoman ? ' ' : ''}
                                <span data-testid={`reserve-confirm-confirm-flight-first-name-roman-${i}`}>
                                  {traveler.flightFirstNameRoman}
                                </span>
                              </div>
                            </div>
                            <SmallGray>
                              ※各航空会社の定めにより予約後の訂正はできないため、キャンセルと新たな予約が必要となります。その場合規定の手数料が発生いたします。
                              {isAiTravel(serviceId) && (
                                <>
                                  手数料の詳細については
                                  <Link href="/charges" target="_blank" isExternal>
                                    旅行業務取扱い料金表
                                  </Link>
                                  をご確認ください。
                                </>
                              )}
                            </SmallGray>
                          </>
                        )}
                        <div className="flex">
                          <div style={styles.label}>性別</div>
                          {traveler.flightGender === 'male' ? (
                            <div style={styles.label} data-testid={`reserve-confirm-confirm-flight-gender-${i}`}>
                              男性
                            </div>
                          ) : traveler.flightGender === 'female' ? (
                            <div style={styles.label} data-testid={`reserve-confirm-confirm-flight-gender-${i}`}>
                              女性
                            </div>
                          ) : null}
                        </div>
                        <div className="flex">
                          <div style={styles.label}>誕生日</div>
                          <div style={styles.label} data-testid={`reserve-confirm-confirm-flight-birthday-${i}`}>
                            {traveler.type === 'companion' || isOwnTraveler(traveler, store)
                              ? moment(traveler.flightBirthday).isValid()
                                ? moment(traveler.flightBirthday).format('YYYY-MM-DD')
                                : ''
                              : '****-**-**(プライバシー保護のため非表示)'}
                          </div>
                        </div>
                        {store.reserveInfo.reservingTrip.isForeignFlightCustomerInfoRequired && (
                          <div className="flex">
                            <div style={styles.label}>国籍</div>
                            <div
                              style={styles.label}
                              data-testid={`reserve-confirm-confirm-flight-nationality-${i}`}
                            >
                              {store.reserveInfo.getNationalityName(traveler.nationalityId)}
                            </div>
                          </div>
                        )}
                        <div className="flex">
                          <div style={styles.label}>緊急連絡先電話番号</div>
                          <div
                            style={styles.label}
                            data-wovn-ignore
                            data-testid={`reserve-confirm-confirm-flight-emergency-tel-${i}`}
                          >
                            {toDomesticTel(traveler.flightTel || '')}
                          </div>
                        </div>
                        {store.reserveInfo.reservingTrip.isForeignFlightCustomerInfoRequired && (
                          <>
                            <div className="flex">
                              <div style={styles.label}>パスポート番号</div>
                              <div
                                style={styles.label}
                                data-wovn-ignore
                                data-testid={`reserve-confirm-confirm-flight-passport-number-${i}`}
                              >
                                {traveler.passportNumber}
                              </div>
                            </div>
                            <div className="flex">
                              <div style={styles.label}>パスポート有効期限</div>
                              <div
                                style={styles.label}
                                data-wovn-ignore
                                data-testid={`reserve-confirm-confirm-flight-passport-expire-${i}`}
                              >
                                {traveler.passportExpire}
                              </div>
                            </div>
                            <div className="flex">
                              <div style={styles.label}>パスポート発行国</div>
                              <div
                                style={styles.label}
                                data-wovn-ignore
                                data-testid={`reserve-confirm-confirm-flight-passport-issued-country-${i}`}
                              >
                                {traveler.passportIssuedCountry}
                              </div>
                            </div>
                          </>
                        )}
                        <div className="flex">
                          <Label>座席の希望</Label>
                          <Label data-testid={`reserve-confirm-confirm-flight-seat-preference-${i}`}>
                            {traveler.airSeat}
                          </Label>
                        </div>
                        {!store.reserveInfo.user.organization?.setting?.disableShowMilageNumber && (
                          <>
                            {(store.reserveInfo.reservingTrip.isForeignFlightCustomerInfoRequired ||
                              traveler.type === 'companion') && (
                              <div className="flex">
                                <Label>マイル番号</Label>
                                <Label
                                  data-wovn-ignore
                                  data-testid={`reserve-confirm-confirm-flight-mileage-${i}`}
                                >
                                  {traveler.mileageNumber}
                                </Label>
                              </div>
                            )}
                            {traveler.mileageNumbers && traveler.mileageNumbers.length > 0 && (
                              <div className="flex">
                                <Label>登録済みマイル番号</Label>
                                <Label
                                  data-wovn-ignore
                                  data-testid={`reserve-confirm-confirm-flight-registered-mileage-${i}`}
                                >
                                  <div>
                                    {traveler.mileageNumbers.map((mileage, i) => (
                                      <div key={i}>
                                        {mileage.carrier}:
                                        {traveler.type === 'companion' || isOwnTraveler(traveler, store)
                                          ? mileage.number
                                          : '**********(プライバシー保護のため非表示)'}
                                      </div>
                                    ))}
                                  </div>
                                </Label>
                              </div>
                            )}
                          </>
                        )}
                        {store.reserveInfo.user.organization?.immigrationCardEnabled && (
                          <div className="flex">
                            <Label>出入国カード作成代行</Label>
                            <Label data-testid={`reserve-confirm-confirm-flight-immigration-card-${i}`}>
                              {traveler.immigrationCardCreation ? 'はい' : 'いいえ'}
                            </Label>
                          </div>
                        )}
                        {store.reserveInfo.user.organization?.visaApplicationEnabled && (
                          <div className="flex">
                            <Label>ビザ申請代行</Label>
                            <Label data-testid={`reserve-confirm-confirm-flight-visa-application-${i}`}>
                              {traveler.visaApplication ? 'はい' : 'いいえ'}
                            </Label>
                          </div>
                        )}
                        <Link href="https://aitravel.jp/knowledges/000001151" target="_blank" isExternal>
                          ビザの必要な国
                        </Link>
                      </div>
                    </div>
                  ))}
                  <StyledSmallRed>
                    {store.reserveInfo.reservingTrip.isForeignFlightCustomerInfoRequired
                      ? '※海外への渡航をご予定の場合、氏名等がパスポートの記載と異なると、当日ご利用いただけない場合がございます。'
                      : '※氏名、誕生日が間違っていると搭乗できない場合がありますのでご注意ください。'}
                  </StyledSmallRed>
                </ChildContent>
              </Section>
            )}
            {store.reserveInfo.reservingTrip.isShinkansenInfoRequired && (
              <Section data-testid="reserve-confirm-confirm-section-shinkansen">
                <Subtitle data-testid="reserve-confirm-confirm-subtitle-shinkansen">新幹線・特急</Subtitle>
                <ChildContent data-testid="reserve-confirm-confirm-content-shinkansen">
                  <div className="flex">
                    {store.reserveInfo.isShinkansenPostalTicketing() && (
                      <>
                        <Label data-testid="reserve-confirm-confirm-shinkansen-address-label">切符送り先</Label>
                        {store.reserveInfo.shinkansenAddressType === 'text' && (
                          <div
                            style={{ display: 'block' }}
                            data-testid="reserve-confirm-confirm-shinkansen-address-type-text"
                          >
                            <Label data-wovn-ignore data-testid="reserve-confirm-confirm-shinkansen-postcode">
                              {store.reserveInfo.postcode}
                            </Label>
                            <Label data-wovn-ignore data-testid="reserve-confirm-confirm-shinkansen-address">
                              {store.reserveInfo.shinkansenAddress}
                            </Label>
                            <Label data-wovn-ignore data-testid="reserve-confirm-confirm-shinkansen-addressee">
                              {store.reserveInfo.shinkansenAddressee}
                            </Label>
                          </div>
                        )}
                        {store.reserveInfo.shinkansenAddressType === 'select' && (
                          <div
                            style={{ display: 'block' }}
                            data-testid="reserve-confirm-confirm-shinkansen-address-type-select"
                          >
                            <Label
                              data-wovn-ignore
                              data-testid="reserve-confirm-confirm-shinkansen-department-label"
                            >
                              {store.reserveInfo.organizationAddress &&
                                store.reserveInfo.organizationAddress.postalCode}
                            </Label>
                            <Label
                              data-wovn-ignore
                              data-testid="reserve-confirm-confirm-shinkansen-department-address"
                            >
                              {store.reserveInfo.organizationAddress &&
                                store.reserveInfo.organizationAddress.location}
                            </Label>
                            <Label
                              data-wovn-ignore
                              data-testid="reserve-confirm-confirm-shinkansen-department-addressee"
                            >
                              {store.reserveInfo.organizationAddress &&
                                store.reserveInfo.organizationAddress.addressee}
                            </Label>
                          </div>
                        )}
                        {store.reserveInfo.shinkansenAddressType === 'organization_base_select' && (
                          <div
                            style={{ display: 'block' }}
                            data-testid="reserve-confirm-confirm-shinkansen-address-type-organization-base-select"
                          >
                            <Label
                              data-wovn-ignore
                              data-testid="reserve-confirm-confirm-shinkansen-organization-base-label"
                            >
                              {store.reserveInfo.organizationBase && store.reserveInfo.organizationBase.postalCode}
                            </Label>
                            <Label
                              data-wovn-ignore
                              data-testid="reserve-confirm-confirm-shinkansen-organization-base-address"
                            >
                              {store.reserveInfo.organizationBase && store.reserveInfo.organizationBase.address}
                            </Label>
                            <Label
                              data-wovn-ignore
                              data-testid="reserve-confirm-confirm-shinkansen-organization-base-addressee"
                            >
                              {store.reserveInfo.organizationBase && store.reserveInfo.organizationBase.addressee}
                            </Label>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {!store.reserveInfo.isExOnlyShinkansenTicketing() &&
                    store.reserveInfo.user.organization?.useQr &&
                    store.reserveInfo.user.organization?.usePostal && (
                      <div className="flex">
                        <Label data-testid="reserve-confirm-confirm-shinkansen-preferred-ticketing-method-label">
                          発券方法
                        </Label>
                        <Label data-testid="reserve-confirm-confirm-shinkansen-preferred-ticketing-method-value">
                          {store.reserveInfo.preferredTicketingMethod === 'postal'
                            ? 'チケット郵送（自宅・職場に届けてもらう）'
                            : store.reserveInfo.preferredTicketingMethod === 'qr'
                            ? 'QR発券（QRコードで受け取る）'
                            : '未設定'}
                        </Label>
                      </div>
                    )}
                  {!store.reserveInfo.isSeparateShinkansenType && (
                    <>
                      {store.reserveInfo.ticket !== 'bulk_ticket' && (
                        <div className="flex">
                          <Label>座席指定</Label>
                          <div>
                            <span data-testid="reserve-confirm-confirm-shinkansen-seat-type">
                              {store.reserveInfo.seatText}
                            </span>
                            <Small>※指定席以外を選択された場合、変更後の料金を改めてご連絡いたします。</Small>
                          </div>
                        </div>
                      )}
                      <div className="flex">
                        <Label>チケットの券種</Label>
                        <div data-testid="reserve-confirm-confirm-shinkansen-ticket-type">
                          {store.reserveInfo.ticketText}
                        </div>
                      </div>
                    </>
                  )}
                  {(store.reserveInfo.seat === 'reserve' ||
                    store.reserveInfo.seat === 'green' ||
                    store.reserveInfo.ticket === 'ex' ||
                    store.reserveInfo.isSeparateShinkansenType) &&
                    store.reserveInfo.reservingTrip.travelers.map((traveler, i) => (
                      <div key={i}>
                        <PeopleNum>
                          {i + 1}人目：{traveler.lastNameRoman} {traveler.firstNameRoman}
                        </PeopleNum>
                        <TravelerBox>
                          {store.reserveInfo.isSeparateShinkansenType && (
                            <>
                              <TravelerInputArea>
                                <Label>チケットの券種</Label>
                                <p data-testid={`reserve-confirm-confirm-shinkansen-ticket-type-${i}`}>
                                  {traveler.getShinkansenTicketTypeText()}
                                </p>
                              </TravelerInputArea>
                              {traveler.shinkansenTicketType !== 'bulk_ticket' && (
                                <TravelerInputArea>
                                  <Label>座席</Label>
                                  <p data-testid={`reserve-confirm-confirm-shinkansen-seat-type-${i}`}>
                                    {traveler.getShinkansenSeatTypeText()}
                                  </p>
                                </TravelerInputArea>
                              )}
                            </>
                          )}
                          {traveler.shinkansenSeatType !== 'free' && (
                            <TravelerInputArea>
                              <Label>座席の希望</Label>
                              <p data-testid={`reserve-confirm-confirm-shinkansen-seat-preference-${i}`}>
                                {traveler.shinkansenSeat}
                              </p>
                              {traveler.shinkansenSeatInfo && (
                                <p data-testid={`reserve-confirm-confirm-shinkansen-seat-preference-other-${i}`}>
                                  :{traveler.shinkansenSeatInfo}
                                </p>
                              )}
                            </TravelerInputArea>
                          )}
                          {(store.reserveInfo.isSeparateShinkansenType
                            ? traveler.shinkansenTicketType
                            : store.reserveInfo.ticket) === 'ex' &&
                            (store.reserveInfo.user.organization?.exicUsageMode === 'user_based' ||
                            traveler.type === 'companion' ? (
                              <>
                                <TravelerInputArea>
                                  <Label>EXIC会員ID</Label>
                                  <p data-testid={`reserve-confirm-confirm-shinkansen-exic-number-${i}`}>
                                    {traveler.exicNumber}
                                  </p>
                                </TravelerInputArea>
                                <TravelerInputArea>
                                  <Label>EXICパスワード</Label>
                                  <p data-testid={`reserve-confirm-confirm-shinkansen-exic-password-${i}`}>
                                    {traveler.exicPasswordType === 'password'
                                      ? traveler.exicPassword?.replace(/./g, '*')
                                      : traveler.exicPassword}
                                  </p>
                                  <span
                                    onClick={async () => {
                                      traveler.exicPasswordType =
                                        traveler.exicPasswordType === 'password' ? 'text' : 'password';
                                    }}
                                  >
                                    {traveler.exicPasswordType === 'password' ? (
                                      <VisibilityIcon />
                                    ) : (
                                      <VisibilityOffIcon />
                                    )}
                                  </span>
                                </TravelerInputArea>
                              </>
                            ) : store.reserveInfo.user.organization?.exicUsageMode === 'card_based' ? (
                              <TravelerInputArea>
                                <Label>EXIC会員ID</Label>
                                <div data-testid={`reserve-confirm-confirm-shinkansen-exic-number-${i}`}>
                                  {store.exicCards && traveler.exicCardId
                                    ? store.exicCards.find(o => o.id === traveler.exicCardId)?.exicNumber ??
                                      '設定なし'
                                    : '設定なし'}
                                </div>
                              </TravelerInputArea>
                            ) : null)}
                        </TravelerBox>
                      </div>
                    ))}
                </ChildContent>
              </Section>
            )}
            {store.reserveInfo.reservingTrip.isHotelInfoRequired && (
              <Section data-testid="reserve-confirm-confirm-section-hotel">
                <Subtitle data-testid="reserve-confirm-confirm-subtitle-hotel">宿泊</Subtitle>
                {store.reserveInfo.hotels.map((hotel, i) => (
                  <div key={`hotel_${hotel.name}_${i}`} data-testid={`reserve-confirm-confirm-hotel-item-${i}`}>
                    <HotelName>{hotel.name}</HotelName>
                    {hotel.rooms &&
                      hotel.rooms.map((room, j) => (
                        <div
                          key={`hotel_${i}_room_${j}_${room.hotelLastName || j}`}
                          data-testid={`reserve-confirm-confirm-hotel-room-${i}-${j}`}
                        >
                          <RoomNum>{j + 1}室目</RoomNum>
                          <ChildContent data-testid={`reserve-confirm-confirm-content-hotel-${i}-${j}`}>
                            <div className="flex">
                              <Label>宿泊代表者氏名（カナ）</Label>
                              <Label
                                data-wovn-ignore
                                data-testid={`reserve-confirm-confirm-hotel-guest-name-kana-${i}-${j}`}
                              >
                                {room.hotelLastNameKana} {room.hotelFirstNameKana}
                              </Label>
                            </div>
                            <div className="flex">
                              <Label>宿泊代表者氏名（ローマ字）</Label>
                              <Label
                                data-wovn-ignore
                                data-testid={`reserve-confirm-confirm-hotel-guest-name-roman-${i}-${j}`}
                              >
                                {room.hotelLastName} {room.hotelFirstName}
                              </Label>
                            </div>
                            <div className="flex">
                              <Label>宿泊代表者電話番号</Label>
                              <Label
                                data-wovn-ignore
                                data-testid={`reserve-confirm-confirm-hotel-guest-tel-${i}-${j}`}
                              >
                                {toDomesticTel(room.hotelTel)}
                              </Label>
                            </div>
                            {room.bedTypeDescription && (
                              <div className="flex">
                                <Label>ベッドの種類</Label>
                                <Label data-testid={`reserve-confirm-confirm-hotel-bed-type-${i}-${j}`}>
                                  {room.bedTypeDescription}
                                </Label>
                              </div>
                            )}
                          </ChildContent>
                        </div>
                      ))}
                  </div>
                ))}
              </Section>
            )}
            {!store.reserveInfo.reservingTrip.isForeignFlightCustomerInfoRequired &&
              store.reserveInfo.rentalCarAvailable && (
                <Section data-testid="reserve-confirm-confirm-section-rental-car">
                  <Subtitle data-testid="reserve-confirm-confirm-subtitle-rental-car">
                    <SubtitleLeft>レンタカー</SubtitleLeft>
                    <SubtitleRight>※オプション</SubtitleRight>
                  </Subtitle>
                  <ChildContent data-testid="reserve-confirm-confirm-content-rental-car">
                    <TravelerBox>
                      {store.reserveInfo.rentalCarRequired === 'true' && (
                        <>
                          {store.reserveInfo.reservingTrip.rentalCars.map((rentalCar, i) => (
                            <div
                              key={`rentalcar_${i}_${rentalCar.departurePlace || i}`}
                              data-testid={`reserve-confirm-confirm-rental-car-item-${i}`}
                            >
                              <RentalCarOrderTitle>ご依頼内容{i !== 0 && i + 1}</RentalCarOrderTitle>
                              <RentalCarOrderContentConfirm>
                                <div className="reserve-confirm__traveler__rentalcar_input-area-left">
                                  <TravelerRentalCarLabel>出発日時</TravelerRentalCarLabel>
                                  <TravelerInputAreaRentalCarText
                                    data-testid={`reserve-confirm-confirm-rental-car-departure-date-${i}`}
                                  >
                                    {rentalCar.departureTime?.format('YYYY月MM月DD日HH時mm分')}
                                  </TravelerInputAreaRentalCarText>
                                </div>
                                <div className="reserve-confirm__traveler__rentalcar_input-area-left">
                                  <TravelerRentalCarLabel>返却日時</TravelerRentalCarLabel>
                                  <TravelerInputAreaRentalCarText
                                    data-testid={`reserve-confirm-confirm-rental-car-return-date-${i}`}
                                  >
                                    {rentalCar.returnTime?.format('YYYY月MM月DD日HH時mm分')}
                                  </TravelerInputAreaRentalCarText>
                                </div>
                                <div className="reserve-confirm__traveler__rentalcar_input-area-left">
                                  <TravelerRentalCarLabel>出発場所</TravelerRentalCarLabel>
                                  <TravelerInputAreaRentalCarText
                                    data-testid={`reserve-confirm-confirm-rental-car-departure-place-${i}`}
                                  >
                                    {rentalCar.departurePlace}
                                  </TravelerInputAreaRentalCarText>
                                </div>
                                <TravelerInputArea>
                                  <TravelerRentalCarLabel>返却場所</TravelerRentalCarLabel>
                                  <TravelerInputAreaRentalCar>
                                    {rentalCar.returnPlace === 'current' ? (
                                      <RentalCarRadio
                                        data-testid={`reserve-confirm-confirm-rental-car-return-place-${i}`}
                                      >
                                        <span>出発場所へ戻す</span>
                                      </RentalCarRadio>
                                    ) : rentalCar.returnPlace === 'other' ? (
                                      <>
                                        <RentalCarRadio
                                          data-testid={`reserve-confirm-confirm-rental-car-return-place-${i}`}
                                        >
                                          <span>別の場所へ乗り捨てる</span>
                                        </RentalCarRadio>
                                        <RentalCarRadio
                                          data-testid={`reserve-confirm-confirm-rental-car-return-place-name-${i}`}
                                        >
                                          <span>{rentalCar.returnPlaceName}</span>
                                        </RentalCarRadio>
                                      </>
                                    ) : null}
                                  </TravelerInputAreaRentalCar>
                                </TravelerInputArea>
                                <TravelerInputArea>
                                  <TravelerRentalCarLabel>
                                    車種・クラス ※選択いただいたタイプから適切な車両をお選びします。
                                  </TravelerRentalCarLabel>
                                  <TravelerInputAreaRentalCarType>
                                    <RentalCarRadio
                                      data-testid={`reserve-confirm-confirm-rental-car-car-type-${i}`}
                                    >
                                      {rentalCar.carTypeOptions.map(
                                        (c, carTypeIndex) =>
                                          rentalCar.carType === c[1] && (
                                            <p id="car_type_text" key={`car_type_confirm_${c[1]}_${carTypeIndex}`}>
                                              {c[0]}
                                            </p>
                                          )
                                      )}
                                    </RentalCarRadio>
                                    {rentalCar.carType === 'other' && (
                                      <RentalCarRadio
                                        data-testid={`reserve-confirm-confirm-rental-car-car-type-other-${i}`}
                                      >
                                        <p id="car_type_text">{rentalCar.carTypeOther}</p>
                                      </RentalCarRadio>
                                    )}
                                  </TravelerInputAreaRentalCarType>
                                </TravelerInputArea>
                                <TravelerInputArea>
                                  <TravelerRentalCarLabel>運転者氏名（カナ）</TravelerRentalCarLabel>
                                  <TravelerInputAreaRentalCar>
                                    <RentalCarRadio
                                      data-wovn-ignore
                                      data-testid={`reserve-confirm-confirm-rental-car-driver-name-kana-${i}`}
                                    >
                                      {rentalCar.lastNameKana} {rentalCar.firstNameKana}
                                    </RentalCarRadio>
                                  </TravelerInputAreaRentalCar>
                                </TravelerInputArea>
                                <TravelerInputArea>
                                  <TravelerRentalCarLabel>運転者電話番号</TravelerRentalCarLabel>
                                  <TravelerInputAreaRentalCar>
                                    <RentalCarRadio
                                      data-wovn-ignore
                                      data-testid={`reserve-confirm-confirm-rental-car-driver-tel-${i}`}
                                    >
                                      {toDomesticTel(rentalCar.driverTel)}
                                    </RentalCarRadio>
                                  </TravelerInputAreaRentalCar>
                                </TravelerInputArea>
                                <TravelerInputArea>
                                  <TravelerRentalCarLabel>備考</TravelerRentalCarLabel>
                                  <TravelerInputAreaRentalRemarksConfirm>
                                    <span
                                      data-wovn-ignore
                                      data-testid={`reserve-confirm-confirm-rental-car-remarks-${i}`}
                                    >
                                      {rentalCar.remarks}
                                    </span>
                                  </TravelerInputAreaRentalRemarksConfirm>
                                </TravelerInputArea>
                              </RentalCarOrderContentConfirm>
                            </div>
                          ))}
                        </>
                      )}
                    </TravelerBox>
                  </ChildContent>
                </Section>
              )}
            <Section data-testid="reserve-confirm-confirm-section-mail-notification">
              {store.reserveInfo.user.organization?.reserveRequestMailNotificationType === 'selectable_user' ? (
                <>
                  <Subtitle data-testid="reserve-confirm-confirm-mail-notification-selectable-user-subtitle">
                    予約依頼に関するメール通知対象者の選択
                  </Subtitle>
                  <ChildContent data-testid="reserve-confirm-confirm-mail-notification-selectable-user-content">
                    {store.reserveInfo.notifiedUsers.map(
                      (user, i) =>
                        user && (
                          <NotifiedUser
                            data-wovn-ignore
                            key={`notified_user_${user.email}_${i}`}
                          >{`${user.name}(${user.email})`}</NotifiedUser>
                        )
                    )}
                  </ChildContent>
                </>
              ) : (
                <>
                  <Subtitle data-testid="reserve-confirm-confirm-mail-notification-subtitle">
                    予約依頼に関するメール通知の有無
                  </Subtitle>
                  <ChildContent data-testid="reserve-confirm-confirm-mail-notification-content">
                    <div className="flex">
                      {store.reserveInfo.allTravelersNotification ? (
                        <div data-testid="reserve-confirm-confirm-mail-notification-enabled">
                          予約内容を関係者に通知する
                        </div>
                      ) : (
                        <div data-testid="reserve-confirm-confirm-mail-notification-disabled">
                          予約内容を関係者に通知しない
                        </div>
                      )}
                    </div>
                  </ChildContent>
                </>
              )}
            </Section>
            <Section data-testid="reserve-confirm-confirm-section-workflow">
              <Subtitle data-testid="reserve-confirm-confirm-subtitle-workflow">ワークフロー</Subtitle>
              <ChildContent data-testid="reserve-confirm-confirm-content-workflow">
                {!store.reserveInfo.user.organization?.freeeWorkflow && store.reserveInfo.isApprovalRequired && (
                  <>
                    {store.reserveInfo.workflowStyle === 'trip' ? (
                      <>
                        {store.reserveInfo.approveStages.map((stage, i) => (
                          <div className="flex" key={`confirm_approver_stage_${stage.stage}_${i}`}>
                            <Label>{stage.stage}次承認者</Label>
                            <Label
                              data-wovn-ignore
                              data-testid={`reserve-confirm-confirm-workflow-stage-${stage.stage}`}
                            >
                              {stage.approvers.map(a => a?.name).join(', ')}
                            </Label>
                          </div>
                        ))}
                      </>
                    ) : (
                      store.reserveInfo.displayApproverList?.list.map(
                        (a, i) =>
                          (i === 0 ||
                            compareApproveStage(
                              a.approve_stage,
                              store.reserveInfo.displayApproverList?.list[i - 1]?.approve_stage
                            ) > 0) && (
                            <div className="flex" key={`confirm_approver_${a.approve_stage}`}>
                              <Label>{a.approve_stage}次承認者</Label>
                              <Label data-testid={`reserve-confirm-confirm-workflow-stage-${a.approve_stage}`}>
                                {a.approve_stage != null
                                  ? store.reserveInfo.displayApproverList
                                      ?.joinApproverByApproveStage(a.approve_stage)
                                      .join(', ')
                                  : ''}
                              </Label>
                            </div>
                          )
                      )
                    )}
                  </>
                )}

                {/* 海外出張でデフォルトワークフローを使用する場合 */}
                {store.reserveInfo.foreignExist &&
                  !store.reserveInfo.useChargingDepartmentWorkflow &&
                  (!store.reserveInfo.displayApproverList ||
                    store.reserveInfo.displayApproverList.list.length === 0) &&
                  store.reserveInfo.useDefaultWorkflow &&
                  store.reserveInfo.defaultWorkflowApprovers &&
                  Object.keys(store.reserveInfo.defaultWorkflowApprovers).map(stage => (
                    <div className="flex" key={`confirm_default_approver_${stage}`}>
                      <Label>{stage}次承認者</Label>
                      <Label data-testid={`reserve-confirm-confirm-workflow-stage-${stage}`}>
                        {store.reserveInfo.defaultWorkflowApprovers?.[stage].map(a => a.name).join(', ')}
                      </Label>
                    </div>
                  ))}

                {store.reserveInfo.isDestinationBusinessTripShown && (
                  <div className="flex">
                    <Label>出張先</Label>
                    <TravelPurposeLabel
                      data-wovn-ignore
                      data-testid="reserve-confirm-confirm-application-final-destination"
                    >
                      {store.reserveInfo.finalDestination}
                    </TravelPurposeLabel>
                  </div>
                )}
                {store.reserveInfo.isPurposeBusinessTripShown && (
                  <div className="flex">
                    <Label>出張の目的</Label>
                    <TravelPurposeLabel data-wovn-ignore data-testid="reserve-confirm-confirm-application-purpose">
                      {store.reserveInfo.purpose}
                    </TravelPurposeLabel>
                  </div>
                )}
                {store.reserveInfo.isInternalNumberShown && (
                  <div className="flex">
                    <Label>社内管理番号</Label>
                    <Label data-wovn-ignore data-testid="reserve-confirm-confirm-application-internal-number">
                      {store.reserveInfo.internalNumber}
                    </Label>
                  </div>
                )}
                {store.reserveInfo.bakurakuWorkflowRequest && (
                  <div className="flex">
                    <Label>Bakuraku事前申請</Label>
                    <TravelPurposeLabel
                      data-wovn-ignore
                      data-testid="reserve-confirm-confirm-application-bakuraku"
                    >
                      {store.reserveInfo.bakurakuWorkflowRequest.label}
                      {formatBakurakuWorkflowRequestCaption(store.reserveInfo.bakurakuWorkflowRequest) && (
                        <>
                          <br />
                          {formatBakurakuWorkflowRequestCaption(store.reserveInfo.bakurakuWorkflowRequest)}
                        </>
                      )}
                    </TravelPurposeLabel>
                  </div>
                )}
                {store.reserveInfo.isChargingDepartmentShown &&
                  store.reserveInfo.orderItemMappingsDepartmentPart.length >= 1 && (
                    <div className="flex">
                      <Label>費用負担部署</Label>
                      {!store.reserveInfo.showDepartmentDetail && store.reserveInfo.chargingDepartment ? (
                        <Label data-wovn-ignore>
                          {store.reserveInfo.chargingDepartment.code}：{store.reserveInfo.chargingDepartment.name}
                        </Label>
                      ) : (
                        <OrderItemMappingListSection
                          data-wovn-ignore
                          reserveInfo={store.reserveInfo}
                          mappingList={store.reserveInfo.orderItemMappingsDepartmentPart}
                          mode={store.reserveInfo.departmentDetailMode}
                        />
                      )}
                    </div>
                  )}
                {/* - project = reserveInfo.getProject() */}
                {store.reserveInfo.isProjectNameShown &&
                  store.reserveInfo.orderItemMappingsProjectPart.length >= 1 && (
                    <div className="flex">
                      <Label>プロジェクト</Label>
                      {!store.reserveInfo.showProjectDetail && store.reserveInfo.project ? (
                        <TravelPurposeLabel
                          data-wovn-ignore
                          data-testid="reserve-confirm-confirm-application-project"
                        >
                          {store.reserveInfo.project.codeAndName}
                        </TravelPurposeLabel>
                      ) : (
                        <OrderItemMappingListSection
                          data-wovn-ignore
                          reserveInfo={store.reserveInfo}
                          mappingList={store.reserveInfo.orderItemMappingsProjectPart}
                          mode={store.reserveInfo.projectDetailMode}
                        />
                      )}
                    </div>
                  )}
                {store.reserveInfo.expensesAccountTypeAvailable &&
                  store.reserveInfo.isExpensesAcountTypeShown &&
                  store.reserveInfo.orderItemMappingsExpensesPart.length >= 1 && (
                    <div className="flex">
                      <Label>勘定科目</Label>
                      {!store.reserveInfo.showExpensesAccountTypeDetail ? (
                        <Label
                          data-wovn-ignore
                          data-testid="reserve-confirm-confirm-application-expenses-account-type"
                        >
                          {
                            store.reserveInfo.orderItemMappingsExpensesPart.find(
                              m => m.travelerId === -1 && m.orderItemType === 'all' && m.orderItemIndex === -1
                            )?.selectedOptionLabel
                          }
                        </Label>
                      ) : (
                        <div data-testid="reserve-confirm-confirm-application-expenses-account-type">
                          <OrderItemMappingListSection
                            data-wovn-ignore
                            reserveInfo={store.reserveInfo}
                            mappingList={store.reserveInfo.orderItemMappingsExpensesPart}
                            mode={store.reserveInfo.expensesAccountTypeDetailMode}
                          />
                        </div>
                      )}
                    </div>
                  )}
                {store.reserveInfo.approveItems.map((d, i) => (
                  <div
                    className="flex"
                    key={d.id}
                    data-testid={`reserve-confirm-confirm-application-custom-item-${i}`}
                  >
                    {d.dataType === 'label' ? (
                      <ApproveItemLabelOnly style={{ maxWidth: '100%', wordWrap: 'break-word' }}>
                        <Markdown markdownText={d.userDisplayName} openLinksNewTab />
                        {d.description && (
                          <ApproveItemDescription data-wovn-ignore>{d.description}</ApproveItemDescription>
                        )}
                      </ApproveItemLabelOnly>
                    ) : (
                      <>
                        <Label>{d.userDisplayName}</Label>
                        <Label data-wovn-ignore>
                          {store.reserveInfo.approveItemValueCodes.get(d.id) && (
                            <>{store.reserveInfo.approveItemValueCodes.get(d.id)}：</>
                          )}
                          {store.reserveInfo.approveItemValues.get(d.id)}
                          {d.description && <ApproveItemDescription>{d.description}</ApproveItemDescription>}
                        </Label>
                      </>
                    )}
                  </div>
                ))}
              </ChildContent>
            </Section>
            {serviceId === 1 && (
              <Section data-testid="reserve-confirm-confirm-section-payment">
                <Subtitle data-testid="reserve-confirm-confirm-subtitle-payment">支払い方法</Subtitle>
                <ChildContent data-testid="reserve-confirm-confirm-content-payment">
                  {store.reserveInfo.paymentMethod === 'billing' ? (
                    <Label data-testid="reserve-confirm-confirm-payment-selected-method-billing">
                      {store.reserveInfo.user.organization?.name}宛に請求
                    </Label>
                  ) : store.reserveInfo.paymentMethod === 'card' &&
                    store.reserveInfo.paymentTransaction?.uid === 'new' ? (
                    <Label data-testid="reserve-confirm-confirm-payment-selected-method-card-new">
                      新しいクレジットカード *****{store.reserveInfo.cardNum04}
                    </Label>
                  ) : (
                    <Label data-testid="reserve-confirm-confirm-payment-selected-method-card-registered">
                      クレジットカード {store.reserveInfo.cardName}
                    </Label>
                  )}
                </ChildContent>
              </Section>
            )}
            {store.reserveInfo.foreignExist && <TabiregiSection reserveInfo={store.reserveInfo} />}
            <Section>
              <Small>
                {store.reserveInfo.reservingTrip.packageType === 'airPackage' && theme.themeClass === 'mynavi' ? (
                  <div data-testid="reserve-confirm-confirm-package-air-note">
                    ※予約の完了後、旅行者名の変更や日程・宿泊施設および航空便の変更など、すべての変更は一切お受けできません。
                    <br />
                    ※当商品は時価販売の旅行商品のため、同一内容でも予約のタイミングにより旅行代金が異なる場合があります。
                    <br />
                    ※宿泊施設によってはフロントにて、 宿泊税・入湯税の現金払いが、必要な場合がございます。
                    <br />
                    ※取消を行う場合は、タイミングに応じて、下記の取消料が発生します。
                    <br />
                    【取消料】
                    <br />
                    <Table>
                      <tr>
                        <LeftTd>
                          a)旅行契約の締結時から旅行開始日の前日から起算してさかのぼって 21 日目にあたる日まで
                        </LeftTd>
                        <RightTd>旅行契約解除時の航空券取消料の額</RightTd>
                      </tr>
                      <tr>
                        <LeftTd>
                          b)旅行開始日の前日から起算してさかのぼって
                          <br />
                          20日~8日前まで
                        </LeftTd>
                        <RightTd>
                          旅行代金の20％又は下記『航空券取消料』の
                          <br />
                          金額のどちらか高い方の金額
                        </RightTd>
                      </tr>
                      <tr>
                        <LeftTd>
                          c)旅行開始日の前日から起算してさかのぼって
                          <br />
                          7日~2日前まで
                        </LeftTd>
                        <RightTd>
                          旅行代金の30％又は下記『航空券取消料』の
                          <br />
                          金額のどちらか高い方の金額
                        </RightTd>
                      </tr>
                      <tr>
                        <LeftTd>d)旅行開始日の前日</LeftTd>
                        <RightTd>
                          旅行代金の40％又は下記『航空券取消料』の
                          <br />
                          金額のどちらか高い方の金額
                        </RightTd>
                      </tr>
                      <tr>
                        <LeftTd>e)旅行開始日の当日(fを除く)</LeftTd>
                        <RightTd>
                          旅行代金の40％又は下記『航空券取消料』の
                          <br />
                          金額のどちらか高い方の金額
                        </RightTd>
                      </tr>
                      <tr>
                        <LeftTd>f)旅行開始後の解除 無連絡不参加</LeftTd>
                        <RightTd>旅行代金の100％</RightTd>
                      </tr>
                    </Table>
                    <br />
                    【航空券取消料】(１区間あたり)
                    <br />
                    <Table>
                      <tr>
                        <LeftTd>搭乗予定日の55日前まで</LeftTd>
                        <RightTd>500円</RightTd>
                      </tr>
                      <tr>
                        <LeftTd>搭乗予定日の54日前～21日前まで</LeftTd>
                        <RightTd>2,000円</RightTd>
                      </tr>
                      <tr>
                        <LeftTd>搭乗予定日の20日前～8日前まで</LeftTd>
                        <RightTd>3,000円</RightTd>
                      </tr>
                      <tr>
                        <LeftTd>搭乗予定日の7日前～1日前まで</LeftTd>
                        <RightTd>6,000円</RightTd>
                      </tr>
                      <tr>
                        <LeftTd>搭乗当日の予定便出発時刻まで</LeftTd>
                        <RightTd>9,000円</RightTd>
                      </tr>
                    </Table>
                    <br />
                    東京都知事登録旅行業 第2-6519号 (社)日本旅行業協会[JATA]正会員
                    <br />
                    株式会社マイナビ
                    <br />
                    〒100‐0003 東京都千代田区一ツ橋1-1-1 パレスサイドビル
                    <br />
                    総合旅行業務取扱管理者 : 木野田拓也
                    <br />
                    <br />
                    総合旅行業務取扱管理者とは、お客様の旅行を取扱う旅行会社・営業所での取引に関する責任者です。
                    <br />
                    ご旅行の契約に関し担当者からの説明にご不明の点がありましたら、最終的には上記取扱管理者がご説明いたします。
                  </div>
                ) : store.reserveInfo.reservingTrip.packageType === 'airPackage' ? (
                  <div data-testid="reserve-confirm-confirm-package-air-note">
                    <SmallIndented>
                      ※キャンセル料は、ご出発の前日から起算して24日前より発生致します。
                    </SmallIndented>
                    <SmallIndented>
                      ※予約後のフライト変更はご出発の前日から起算して25日前迄であれば無料で承ります。24日前より手数料が発生致しますのでご注意下さいませ。
                    </SmallIndented>
                    <SmallIndented>
                      ※ご予約後に変更(参加者変更、人数変更、日程変更等)が生じる際は、ご予約をお取消後に再度お申込み頂く必要がございます。空席・空室状況により同内容でご予約できない場合もございますのでご了承ください。
                    </SmallIndented>
                    {store.reserveInfo.packageProvider !== 'rakuten' && (
                      <div data-testid="reserve-confirm-confirm-package-wbf-block">
                        <br />
                        旅行企画実施
                        <br />
                        国土交通大臣登録旅行業第１３８３号
                        <br />
                        株式会社ホワイト・ベアーファミリー
                        <br />
                        〒531-0072 大阪市北区豊崎3-14-9 WBFビル
                        <br />
                        <br />
                        受託販売
                        <br />
                        {store.reserveInfo.consignmentSale?.travelAgencyNumber}
                        <br />
                        {store.reserveInfo.consignmentSale?.companyName}
                        <br />〒{store.reserveInfo.consignmentSale?.postcode}{' '}
                        {store.reserveInfo.consignmentSale?.address}
                        <br />
                      </div>
                    )}
                  </div>
                ) : store.reserveInfo.reservingTrip.packageType === 'jrPackage' ? (
                  <div data-testid="reserve-confirm-confirm-package-jr-block">
                    旅行企画実施
                    <br />
                    観光庁長官登録　旅行業第1135号
                    <br />
                    株式会社びゅうトラベルサービス
                    <br />
                    〒130-0013 東京都墨田区錦糸3-2-1
                    <br />
                  </div>
                ) : store.reserveInfo.isApprovalRequired && serviceId === 1 ? (
                  <>
                    出張事前申請機能をご利用の場合、申請時から承認時にかけて旅行商品の商品情報、価格、および状態が更新される場合があります。
                    <br />
                    申請が承認された際、申請時点と承認時点で内容に差分がある場合以下のメール本文で更新内容を記載します。
                    <br />
                    メール件名：「【AI Travel】出張が承認されました（出張者：XXXXX）」
                    <br />
                    <br />
                    出張申請承認後からご依頼いただいた各商品のキャンセル規程が適用されます。
                    <br />
                    ご依頼いただいた各商品について、在庫情報のタイムラグ等によりご希望に添えなかった場合は代替案をご連絡いたしますのでご了承ください。
                    <br />
                    ユーザーは、キャンセル時に株主優待券を受領した場合、弊社に返却の必要があることをご了承ください。
                  </>
                ) : serviceId === 1 ? (
                  <>
                    予約（決済）完了後からご依頼いただいた各商品のキャンセル規程が適用されます。
                    <br />
                    ご依頼いただいた各商品について、在庫情報のタイムラグ等によりご希望に添えなかった場合は代替案をご連絡いたしますのでご了承ください。
                    <br />
                    ユーザーは、キャンセル時に株主優待券を受領した場合、弊社に返却の必要があることをご了承ください。
                  </>
                ) : (
                  <>
                    予約（決済）完了後からご依頼いただいた各商品のキャンセル規程が適用されます。
                    <br />
                    ご依頼いただいた各商品について、在庫情報のタイムラグ等によりご希望に添えなかった場合は代替案をご連絡いたしますのでご了承ください。
                  </>
                )}
                <br />
                <br />
                {theme.themeClass === 'mynavi' && (
                  <>
                    予約依頼時に、
                    <Link href="https://btm.mynavi.jp/terms/pdf/hojinkiyaku.pdf" target="_blank" rel="noopener">
                      マイナビBTM会員向け利用規約
                    </Link>
                    、
                    <Link href="https://btm.mynavi.jp/terms/pdf/yakkan_arrange.pdf" target="_blank" rel="noopener">
                      標準旅行業約款
                    </Link>
                    、
                    <Link href="https://btm.mynavi.jp/terms/pdf/privacypolicy.pdf" target="_blank" rel="noopener">
                      個人情報の取り扱い
                    </Link>
                    について同意するものとします。
                    <br />
                    <label>
                      <input
                        type="checkbox"
                        className="terms_agree"
                        data-testid="reserve-confirm-confirm-agreement-checkbox-mynavi"
                        checked={store.reserveInfo.termsAgree}
                        onChange={() => {
                          store.reserveInfo.termsAgree = !store.reserveInfo.termsAgree;
                        }}
                      />
                      上記に同意する
                    </label>
                  </>
                )}
                {serviceId === 1 && theme.themeClass !== 'mynavi' && (
                  <>
                    {store.reserveInfo.isApprovalRequired ? '出張事前申請時' : '予約（決済）時'}に弊社の
                    <Link href="https://aitravel.cloud/tos/" target="_blank" rel="noopener" isExternal>
                      利用約款
                    </Link>
                    、
                    <Link
                      href="https://d14vxaqxdw6ji3.cloudfront.net/images/travel_terms.pdf"
                      target="_blank"
                      rel="noopener"
                      isExternal
                    >
                      旅行業約款
                    </Link>
                    ・
                    <Link
                      href="https://d14vxaqxdw6ji3.cloudfront.net/images/requirements.pdf"
                      target="_blank"
                      rel="noopener"
                      isExternal
                    >
                      手配旅行条件書
                    </Link>
                    、
                    <Link
                      href="https://transferdata.co.jp/privacy-policy#a"
                      target="_blank"
                      rel="noopener"
                      isExternal
                    >
                      プライバシーポリシー
                    </Link>
                    に同意するものとします。
                    <br />
                    本依頼を行う者は、本サービスを正当に利用する権限を有することを保証します。
                    <br />
                    <label>
                      <input
                        type="checkbox"
                        className="terms_agree"
                        data-testid="reserve-confirm-confirm-agreement-checkbox-default"
                        checked={store.reserveInfo.termsAgree}
                        onChange={() => {
                          store.reserveInfo.termsAgree = !store.reserveInfo.termsAgree;
                        }}
                      />
                      上記に同意する
                    </label>
                  </>
                )}
                {serviceId === 2 && theme.themeClass !== 'mynavi' && (
                  <>
                    予約（決済）時に弊社の
                    <Link href="https://www.tabikobo.com/policy/" target="_blank" rel="noopener" isExternal>
                      プライバシーポリシー
                    </Link>
                    に同意するものとします。
                    <br />
                    本依頼を行う者は、本サービスを正当に利用する権限を有することを保証します。
                    <br />
                    <label>
                      <input
                        type="checkbox"
                        className="terms_agree"
                        data-testid="reserve-confirm-confirm-agreement-checkbox-tabikobo"
                        checked={store.reserveInfo.termsAgree}
                        onChange={() => {
                          store.reserveInfo.termsAgree = !store.reserveInfo.termsAgree;
                        }}
                      />
                      上記に同意する
                    </label>
                  </>
                )}
              </Small>
            </Section>
          </ChildWrapper>
          <div>
            {store.paymentLoading ? (
              <PaymentLoadingImg src="/images/loading.gif" width={50} height={50} />
            ) : (
              <>
                {store.paymentFailed && store.paymentErrors && (
                  <StyledPaymentError>
                    {typeof store.paymentErrors === 'string' ? (
                      <pre
                        dangerouslySetInnerHTML={{
                          __html: parsePaymentError(store.paymentErrors, theme.linkColor)
                        }}
                        style={{ background: 'none', padding: 0, border: 'none' }}
                      />
                    ) : (
                      <p>{store.paymentErrors}</p>
                    )}
                    {store.shouldShowSearchLinkingErrorKnowledgeLink && serviceId === 1 && (
                      <Link
                        href="https://aitravel.jp/knowledges/000001106"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginTop: '10px', display: 'block' }}
                      >
                        エラーメッセージが表示されて、交通機関・ホテルの予約ができない場合はどうすればいいですか？
                      </Link>
                    )}
                  </StyledPaymentError>
                )}
                {/* {disconnected && (
            <div className="error">
              通信環境が不安定です。
              <br />
              時間をおいてもう一度お試しください。
            </div>
          )} */}
                <ButtonArea>
                  <Link
                    href="/"
                    style={{ margin: 'auto 0' }}
                    data-testid="reserve-confirm-confirm-back-button"
                    onClick={e => {
                      if (handleBackToStep1Click) {
                        e.preventDefault();
                        handleBackToStep1Click();
                      } else {
                        store.stepIndex = 0;
                      }
                    }}
                  >
                    {'< 戻る'}
                  </Link>
                  <ConfirmButtons>
                    {handleSaveDraft && (
                      <Button color="sub" onClick={handleSaveDraft}>
                        下書き保存
                      </Button>
                    )}
                    <Button
                      style={{ marginLeft: 15 }}
                      type="button"
                      role="confirm"
                      data-testid="reserve-confirm-confirm-submit-button"
                      onClick={e => {
                        if (
                          store.reserveInfo.tripDuplications.length > 0 ||
                          store.reserveInfo.reservingTrip.restrictedHotels.length > 0 ||
                          store.reserveInfo.passportWarningTravelersForUi.length > 0
                        ) {
                          setIsConfirmOpen(true);
                        } else {
                          handleConfirmed(e);
                        }
                      }}
                      disabled={!store.reserveInfo.termsAgree}
                    >
                      この内容で手配を依頼
                    </Button>
                  </ConfirmButtons>
                </ButtonArea>
              </>
            )}
          </div>
          <ConfirmCustomerConfirmModal
            open={isConfirmOpen}
            reserveInfo={store.reserveInfo}
            onClose={() => setIsConfirmOpen(false)}
            onSubmit={handleConfirmed}
          />
        </>
      </StylesContext.Provider>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

const StylesContext = React.createContext<ReturnType<typeof getStyles> | null>(null);
const fallbackStyles = getStyles(createAITTheme());
const useStylesContext = () => useContext(StylesContext) ?? fallbackStyles;

const Title: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={styles.title}>
      {children}
    </div>
  );
};

const ChildWrapper: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={styles.childWrapper} className="reserve-confirm-customer-info">
      {children}
    </div>
  );
};

const Section: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={styles.section}>
      {children}
    </div>
  );
};

const Subtitle: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={styles.subtitle}>
      {children}
    </div>
  );
};

const SubtitleLeft: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.subtitleLeft}>{children}</div>;
};

const SubtitleRight: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.subtitleRight}>{children}</div>;
};

const ChildContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={styles.childContent}>
      {children}
    </div>
  );
};

const TravelerBox: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.travelerBox}>{children}</div>;
};

const TravelerPeopleNum: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <p style={styles.travelerPeopleNum}>{children}</p>;
};

const TravelerInputArea: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.travelerInputArea}>{children}</div>;
};

const Label: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div style={styles.label} {...props}>
      {children}
    </div>
  );
};

const ApproveItemLabelOnly: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, style, ...props }) => {
  const styles = useStylesContext();
  return (
    <div style={{ ...styles.approveItemLabelOnly, ...style }} {...props}>
      {children}
    </div>
  );
};

const ApproveItemDescription: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div style={styles.approveItemDescription} {...props}>
      {children}
    </div>
  );
};

const TravelPurposeLabel: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div style={styles.travelPurposeLabel} {...props}>
      {children}
    </div>
  );
};

const PeopleNum: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <p style={styles.peopleNum}>{children}</p>;
};

const Small: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.small}>{children}</div>;
};

const SmallIndented: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.smallIndented}>{children}</div>;
};

const StyledSmallRed: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.styledSmallRed}>{children}</div>;
};

const SmallGray: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.smallGray}>{children}</div>;
};

const HotelName: React.FC = ({ children }) => {
  return <p className="reserve-confirm-customer-info__hotel-name">{children}</p>;
};

const RoomNum: React.FC = ({ children }) => {
  return <p className="reserve-confirm-customer-info__room-num">{children}</p>;
};

const RentalCarOrderTitle: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <p style={styles.rentalCarOrderTitle}>{children}</p>;
};

const RentalCarOrderContentConfirm: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return (
    <div
      style={styles.rentalCarOrderContentConfirm}
      className="reserve-confirm-customer-info__rental-car-order-content-confirm"
    >
      {children}
    </div>
  );
};

const TravelerRentalCarLabel: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.travelerRentalCarLabel}>{children}</div>;
};

const TravelerInputAreaRentalCar: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.travelerInputAreaRentalCar, ...props.style }}>
      {children}
    </div>
  );
};

const TravelerInputAreaRentalCarText: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  ...props
}) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.travelerInputAreaRentalCarText, ...props.style }}>
      {children}
    </div>
  );
};

const TravelerInputAreaRentalCarType: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  ...props
}) => {
  const styles = useStylesContext();
  return (
    <div
      {...props}
      style={{ ...styles.travelerInputAreaRentalCarType, ...props.style }}
      className="reserve-confirm-customer-info__traveler-input-area-rental-car-type"
    >
      {children}
    </div>
  );
};

const TravelerInputAreaRentalRemarksConfirm: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  ...props
}) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.travelerInputAreaRentalRemarksConfirm, ...props.style }}>
      {children}
    </div>
  );
};

const RentalCarRadio: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.rentalCarRadio, ...props.style }}>
      {children}
    </div>
  );
};

const NotifiedUser: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.notifiedUser}>{children}</div>;
};

const PaymentLoadingImg: React.FC<React.ImgHTMLAttributes<HTMLImageElement>> = props => {
  const styles = useStylesContext();
  return <img style={styles.paymentLoadingImg} {...props} />;
};

const StyledPaymentError: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.styledPaymentError}>{children}</div>;
};

const ButtonArea: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.buttonArea}>{children}</div>;
};

const ConfirmButtons: React.FC = ({ children }) => {
  const styles = useStylesContext();
  return <div style={styles.confirmButtons}>{children}</div>;
};

const Table: React.FC<React.TableHTMLAttributes<HTMLTableElement>> = ({ children, style, ...props }) => {
  const styles = useStylesContext();
  return (
    <table {...props} style={{ ...styles.table, ...style }}>
      {children}
    </table>
  );
};

const LeftTd: React.FC<React.TdHTMLAttributes<HTMLTableCellElement>> = ({ children, style, ...props }) => {
  const styles = useStylesContext();
  return (
    <td {...props} style={{ ...styles.leftTd, ...style }}>
      {children}
    </td>
  );
};

const RightTd: React.FC<React.TdHTMLAttributes<HTMLTableCellElement>> = ({ children, style, ...props }) => {
  const styles = useStylesContext();
  return (
    <td {...props} style={{ ...styles.rightTd, ...style }}>
      {children}
    </td>
  );
};

function getStyles(theme: ReturnType<typeof useAITTheme>['theme']) {
  return {
    title: { marginBottom: 10, fontSize: 18, fontWeight: 'bold' as const, paddingBottom: 10 },
    childWrapper: { fontSize: 13, marginBottom: 20 },
    section: { paddingBottom: 20 },
    subtitle: { fontSize: 15, margin: '20px 0 10px', fontWeight: 'bold' as const },
    subtitleLeft: { float: 'left' as const, paddingRight: 5 },
    subtitleRight: { float: 'left' as const, fontSize: 13, fontWeight: 'normal' as const },
    childContent: { fontSize: 13, marginLeft: 10 },
    travelerBox: { marginBottom: 15, marginLeft: 10 },
    travelerPeopleNum: { fontSize: 13 },
    travelerInputArea: { display: 'flex' as const, flexWrap: 'wrap' as const, marginBottom: 5 },
    label: { fontSize: 13, width: 200, lineHeight: '34px' as const },
    approveItemLabelOnly: { fontSize: 13, marginBottom: 5, lineHeight: '34px' as const },
    approveItemDescription: {
      marginTop: 6,
      color: theme.tokens.colors.text.description,
      lineHeight: '18px' as const,
      whiteSpace: 'pre-wrap' as const
    },
    travelPurposeLabel: {
      fontSize: 13,
      width: 400,
      lineHeight: '16px' as const,
      whiteSpace: 'pre-line' as const,
      overflowWrap: 'break-word' as const
    },
    peopleNum: { fontSize: 13, marginTop: 10 },
    small: { fontSize: 11 },
    smallIndented: { textIndent: '-14px', marginLeft: 14 },
    styledSmallRed: { fontSize: 11, color: theme.tokens.colors.danger.primary },
    smallGray: { fontSize: 11, color: theme.tokens.colors.text.description },
    rentalCarOrderTitle: { margin: '10px 0 5px' },
    rentalCarOrderContentConfirm: {
      marginLeft: 0,
      paddingLeft: 20,
      position: 'relative' as const,
      borderLeft: `3px solid ${theme.tokens.colors.border.field}`
    },
    travelerRentalCarLabel: {
      lineHeight: '34px' as const,
      display: 'flex' as const,
      float: 'left' as const
    },
    travelerInputAreaRentalCar: { lineHeight: '34px' as const },
    travelerInputAreaRentalCarText: { lineHeight: '34px' as const, marginLeft: 73, marginTop: 5 },
    travelerInputAreaRentalCarType: { lineHeight: '34px' as const, marginLeft: 52 },
    travelerInputAreaRentalRemarksConfirm: { lineHeight: '34px' as const, marginLeft: 45 },
    rentalCarRadio: { marginLeft: 20 },
    notifiedUser: { margin: '0 0 10px -10px' },
    paymentLoadingImg: { display: 'block', margin: '0 auto' },
    styledPaymentError: {
      color: theme.tokens.colors.danger.primary,
      marginTop: 10,
      wordBreak: 'break-all' as const,
      whiteSpace: 'pre-wrap' as const
    },
    buttonArea: { display: 'flex', justifyContent: 'space-between' },
    confirmButtons: { display: 'flex' },
    table: { marginTop: 10, fontSize: 11, width: '90%' },
    leftTd: { background: '#d1d1d1', border: '1px solid #eee', paddingLeft: 10 },
    rightTd: { paddingLeft: 10, border: '1px solid #eee' }
  };
}

export default observer(ConfirmCustomerInformation);
